import React from "react";
import { motion } from "framer-motion";
import { Copy, Check, ArrowUpRight, ChevronDown } from "lucide-react";
import { ASSETS, CATS, CA, PUMP_URL, X_URL, TELEGRAM_URL, TIKTOK_URL } from "../../data/pusheen";
import { useSound } from "../../sound/SoundContext";
import { burst, burstFromEvent } from "../../lib/burst";
import { toast } from "sonner";

const rise = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 0.1 * i, type: "spring", stiffness: 200, damping: 20 } }),
};

export default function Hero() {
  const { play } = useSound();
  const [copied, setCopied] = React.useState(false);

  const copyCA = async () => {
    try {
      await navigator.clipboard.writeText(CA);
    } catch {
      const t = document.createElement("textarea");
      t.value = CA; document.body.appendChild(t); t.select();
      document.execCommand("copy"); document.body.removeChild(t);
    }
    setCopied(true);
    play("sparkle");
    burst(window.innerWidth / 2, window.innerHeight * 0.72, { count: 18, kinds: ["heart", "star"] });
    toast.custom(() => (
      <div className="flex items-center gap-3 bg-pusheen-cream border-[3px] border-pusheen-brown rounded-2xl px-4 py-3 shadow-[4px_4px_0_#4A3728]">
        <img src={CATS.heart} alt="" className="w-10 h-10" />
        <div>
          <p className="font-display font-extrabold text-pusheen-brown leading-tight">Copied, nya! 💖</p>
          <p className="font-body text-sm text-pusheen-brownlight">Contract address is on your clipboard.</p>
        </div>
      </div>
    ), { duration: 2600 });
    setTimeout(() => setCopied(false), 2000);
  };

  const petHero = (e) => {
    play("meow");
    burstFromEvent(e, { count: 10, kinds: ["heart"] });
  };

  return (
    <header className="relative z-10 min-h-screen flex items-center justify-center pt-28 pb-16 px-4">
      <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
        <motion.img
          variants={rise} initial="hidden" animate="show" custom={1}
          src={ASSETS.logo}
          alt="Pusheen the Cat"
          className="w-[90%] sm:w-[74%] max-w-xl select-none"
          draggable="false"
        />

        <motion.button
          variants={rise} initial="hidden" animate="show" custom={2}
          onClick={petHero}
          data-testid="hero-mascot"
          aria-label="Pet Pusheen"
          className="mt-2 focus:outline-none"
        >
          <img
            src={CATS.wave}
            alt="Pusheen waving"
            className="w-44 sm:w-56 animate-bob drop-shadow-[6px_8px_0_rgba(74,55,40,0.12)] hover:scale-105 active:scale-95 transition-transform cursor-pointer"
            draggable="false"
          />
        </motion.button>

        <motion.h1
          variants={rise} initial="hidden" animate="show" custom={3}
          className="font-script text-4xl sm:text-5xl lg:text-6xl text-pusheen-brown mt-4 leading-tight"
        >
          A decade of internet culture.
        </motion.h1>
        <motion.p
          variants={rise} initial="hidden" animate="show" custom={4}
          className="font-body text-lg sm:text-xl text-pusheen-brownlight mt-3 max-w-xl"
        >
          Nearly <strong className="text-pusheen-brown">60 billion</strong> GIPHY views. Millions of people have shared Pusheen to celebrate, comfort, and connect.
        </motion.p>

        <motion.div
          variants={rise} initial="hidden" animate="show" custom={5}
          className="flex flex-wrap items-center justify-center gap-3 mt-8"
        >
          <a
            href={PUMP_URL} target="_blank" rel="noopener noreferrer"
            onClick={() => play("yay")}
            data-testid="hero-buy-button"
            className="group bg-pusheen-pink text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-6 sm:px-8 py-3 sm:py-3.5 font-display font-extrabold text-lg sm:text-xl shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-pinklight hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all inline-flex items-center gap-2"
          >
            Buy on pump.fun
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href={X_URL} target="_blank" rel="noopener noreferrer"
            onClick={() => play("pop")}
            data-testid="hero-x-button"
            className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-6 py-3 sm:py-3.5 font-display font-extrabold text-lg sm:text-xl shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-cream hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all inline-flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Follow
          </a>
          <a
            href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer"
            onClick={() => play("pop")}
            data-testid="hero-telegram-button"
            className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-6 py-3 sm:py-3.5 font-display font-extrabold text-lg sm:text-xl shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-cream hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all inline-flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
            </svg>
            Telegram
          </a>
          <a
            href={TIKTOK_URL} target="_blank" rel="noopener noreferrer"
            onClick={() => play("pop")}
            data-testid="hero-tiktok-button"
            className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-6 py-3 sm:py-3.5 font-display font-extrabold text-lg sm:text-xl shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-cream hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all inline-flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
              <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
            </svg>
            TikTok
          </a>
        </motion.div>

        <motion.div
          variants={rise} initial="hidden" animate="show" custom={6}
          className="mt-6 w-full max-w-lg"
        >
          <p className="font-body text-sm font-semibold text-pusheen-brownlight mb-2">Contract Address</p>
          <button
            onClick={copyCA}
            data-testid="copy-ca-button"
            className="w-full group bg-white border-[3px] border-pusheen-brown rounded-2xl px-4 py-3 shadow-[4px_4px_0_#4A3728] hover:-translate-y-0.5 active:translate-y-[4px] active:shadow-none transition-all flex items-center justify-between gap-3"
          >
            <code data-testid="ca-value" className="font-mono text-xs sm:text-sm text-pusheen-brown truncate">{CA}</code>
            <span className="shrink-0 inline-flex items-center gap-1.5 font-display font-bold text-pusheen-brown bg-pusheen-mint border-2 border-pusheen-brown rounded-full px-3 py-1">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </span>
          </button>
        </motion.div>

        <motion.a
          href="#lore"
          onClick={() => play("boop")}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
          className="mt-10 text-pusheen-brownlight flex flex-col items-center gap-1 font-body text-sm"
        >
          scroll for the lore
          <ChevronDown className="w-5 h-5 animate-bob" />
        </motion.a>
      </div>
    </header>
  );
}
